'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { PlpBannerModule } from '@/types/amplience';
import { useIsMultiCouponEnabled } from '@/hooks/useIsMultiCouponEnabled';
import { activeCouponsAtom, promoConfigAtom } from '@/atoms/promoAtom';
import PlpBanner from '@/components/PlpBanner/PlpBanner';
import { PLP_BANNER_DESKTOP_COLUMNS, PLP_BANNER_MOBILE_COLUMNS, shouldShowPlpBanner } from '@/utility/plpBannerVisibility';
type PlpBannerInterstitialProps = {
  banner: PlpBannerModule | null;
  pageIndex: number;
  itemCount: number;
};

/**
 * One full-width grid item on row 2. Products auto-flow around it, so the same
 * slot sits below the first row at 2 columns and at 4 columns.
 * Fewer than two desktop rows (4–7 products) keeps the slot mobile-only.
 * In the single-coupon control of the multi-coupon A/B test, active coupons that don't include
 * the current promo-tool code hide the slot, so the grid row is not left empty.
 */
export default function PlpBannerInterstitial(t0) {
  const $ = _c(10);
  const {
    banner,
    pageIndex,
    itemCount
  } = t0;
  const promoSlot = useAtomValue(promoConfigAtom);
  const activeCoupons = useAtomValue(activeCouponsAtom);
  const isMultiCouponEnabled = useIsMultiCouponEnabled();
  const voucherCode = promoSlot?.content["promo-tool"]?.campaignDetails?.code;
  const hasDifferentActiveCode = !isMultiCouponEnabled && activeCoupons.length > 0 && !activeCoupons.some(c => c.code === voucherCode);
  if (!banner || hasDifferentActiveCode) {
    return null;
  }
  const showOnMobile = shouldShowPlpBanner({
    pageIndex,
    itemCount,
    columns: PLP_BANNER_MOBILE_COLUMNS,
    hasContent: true
  });
  let t1;
  if ($[0] !== itemCount || $[1] !== pageIndex) {
    t1 = shouldShowPlpBanner({
      pageIndex,
      itemCount,
      columns: PLP_BANNER_DESKTOP_COLUMNS,
      hasContent: true
    });
    $[0] = itemCount;
    $[1] = pageIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const showOnDesktop = t1;
  if (!showOnMobile) {
    return null;
  }
  const t2 = !showOnDesktop && "md:hidden";
  let t3;
  if ($[3] !== t2) {
    t3 = clsx("col-span-full row-start-2", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== banner) {
    t4 = <PlpBanner content={banner} />;
    $[5] = banner;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <li className={t3} data-type="PlpBannerSlot">{t4}</li>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}